import { useEffect, useState, type CSSProperties } from 'react'
import { Bell, BellOff, Check, LogOut, Settings } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore, guildMuted } from '@/store/guilds'
import { useSocialStore } from '@/store/social'
import { basePermissions, has, P } from '@/lib/permissions'
import { ChannelType, type Guild, type Member } from '@/types/api'
import { ConfirmModal } from './ConfirmModal'
import { cx } from '@/lib/cx'
import styles from './GuildContextMenu.module.css'
import { publicUser } from '@/lib/auth'

const WIDTH = 220

/**
 * Меню по правой кнопке на сервере в левой колонке: то, за чем чаще всего лезут в меню
 * сервера сверху, — прочитать всё, приглушить, настройки, выход.
 */
export function GuildContextMenu({ guild, x, y, unread, onClose }: { guild: Guild; x: number; y: number; unread: boolean; onClose: () => void }) {
  const me = useAuthStore((s) => s.user)
  const openGuild = useAppStore((s) => s.openGuild)
  const openGuildSettings = useAppStore((s) => s.openGuildSettings)
  const setView = useAppStore((s) => s.setView)
  const member = useGuildStore((s) => (me ? s.members[guild.id]?.[me.id] : undefined))
  const channels = useGuildStore((s) => s.channels[guild.id])
  const settings = useGuildStore((s) => s.settings[guild.id])
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const leaveGuild = useGuildStore((s) => s.leaveGuild)
  const ack = useSocialStore((s) => s.ack)
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    if (leaving) return
    const off = () => onClose()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', off)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', off)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose, leaving])

  if (leaving) {
    return (
      <ConfirmModal
        title={`Покинуть «${guild.name}»`}
        text="Вы уверены, что хотите покинуть сервер? Вернуться можно будет только по новому приглашению."
        confirmLabel="Покинуть сервер"
        onCancel={() => { setLeaving(false); onClose() }}
        onConfirm={async () => {
          await leaveGuild(guild.id)
          setView({ kind: 'home', page: 'friends' })
          onClose()
        }}
      />
    )
  }

  const self: Member | undefined = member ?? (me ? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null } : undefined)
  // права считаем как в списке каналов; участник ещё не загружен — настройки прячем
  const perms = member && self ? basePermissions(guild, self) : 0n
  const canSettings = has(perms, P.MANAGE_GUILD) || has(perms, P.MANAGE_ROLES) || has(perms, P.BAN_MEMBERS) || has(perms, P.KICK_MEMBERS)
  const owner = me?.id === guild.owner_id
  const muted = guildMuted(settings)

  const markRead = () => {
    for (const ch of Object.values(channels ?? {})) {
      if ((ch.type === ChannelType.GuildText || ch.type === ChannelType.GuildVoice || ch.type === ChannelType.GuildForum || ch.type === ChannelType.PublicThread) && ch.last_message_id) void ack(ch.id, ch.last_message_id)
    }
    onClose()
  }

  const left = Math.min(x, window.innerWidth - WIDTH - 8)
  const top = Math.min(y, window.innerHeight - 200)

  return (
    <div
      className={cx(styles.menu, 'klichat-popover')}
      role="menu"
      style={{ left, top, width: WIDTH, '--popover-origin': 'top left' } as CSSProperties}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className={styles.title}>{guild.name}</div>
      <button type="button" role="menuitem" className={styles.item} disabled={!unread} onClick={markRead}>
        <Check size={16} /> Отметить как прочитанное
      </button>
      <button type="button" role="menuitem" className={styles.item} onClick={() => { void updateSettings(guild.id, { muted: !muted }); onClose() }}>
        {muted ? <Bell size={16} /> : <BellOff size={16} />} {muted ? 'Включить уведомления' : 'Заглушить сервер'}
      </button>
      {canSettings ? (
        <button type="button" role="menuitem" className={styles.item} onClick={() => { openGuild(guild.id); openGuildSettings(guild.id); onClose() }}>
          <Settings size={16} /> Настройки сервера
        </button>
      ) : null}
      {owner ? null : (
        <>
          <div className={styles.separator} role="separator" />
          <button type="button" role="menuitem" className={cx(styles.item, styles.danger)} onClick={() => setLeaving(true)}>
            <LogOut size={16} /> Покинуть сервер
          </button>
        </>
      )}
    </div>
  )
}
